// ---------------------------------------------------------------------------
// Is this video frame a document, held still, and in focus?
// ---------------------------------------------------------------------------
//
// Answered ENTIRELY in the browser, on a downscaled greyscale copy of the
// frame. Nothing here touches the network, so the camera can be analysed many
// times a second at zero cost — which is the whole point: a scan costs money,
// so the decision of WHEN to scan has to be free.
//
// Deliberately no OpenCV. use-camera.tsx carries a commented-out cv.js
// experiment; loading a multi-megabyte WASM build to compute one convolution is
// a bad trade on a front-desk tablet. The maths below is about thirty lines.
//
// FOUR SIGNALS, all of which must hold:
//
//   sharpness   variance of the Laplacian. The standard focus measure: a blurred
//               image has little high-frequency content, so the second
//               derivative is flat and its variance collapses.
//   detail      the FRACTION of pixels with a strong Laplacian response. Guards
//               the case sharpness alone gets wrong — a sharply-focused blank
//               desk scores low on both, but a high-contrast pattern with no
//               text can score high on variance. A document is dense in edges.
//   stillness   mean absolute difference against the previous frame. A moving
//               document is motion-blurred in the captured JPEG even when the
//               preview looks fine.
//   exposure    mean luminance inside a usable band. Too dark and the model
//               reads nothing; blown out and the print disappears into glare.

export interface FrameQuality {
  sharpness: number;
  detail: number;
  stillness: number;
  brightness: number;
  /** Fraction of the frame that is skin-toned. High = a person, not a document. */
  skin: number;
  /** Fraction of bright, paper-like pixels. */
  paper: number;
  /** Fraction of mid-tones. High = a face or a scene; documents are bimodal. */
  midtone: number;
  ready: boolean;
  // What to tell the user when it is NOT ready. One reason at a time, in the
  // order worth fixing — telling someone the image is blurry when the real
  // problem is that the room is dark just sends them chasing the wrong thing.
  hint: string;
}

export interface QualityThresholds {
  sharpness: number;
  detail: number;
  stillness: number;
  minBrightness: number;
  maxBrightness: number;
  /** Reject above this much skin. */
  maxSkin: number;
  /** Require at least this much paper-bright area. */
  minPaper: number;
  /** Reject above this much mid-tone. */
  maxMidtone: number;
}

// Starting points, tuned against a phone camera at arm's length over a document
// on a desk. They are exported so they can be adjusted per deployment without
// touching the algorithm — camera hardware and front-desk lighting vary far
// more than the maths does.
export const DEFAULT_THRESHOLDS: QualityThresholds = {
  sharpness: 55,
  detail: 0.012,
  stillness: 6,
  minBrightness: 45,
  maxBrightness: 225,
  // A face fills roughly half the frame with skin when held at scanning
  // distance. A document held by its edges shows a few percent at most.
  maxSkin: 0.22,
  // Card stock and passport pages are pale. This is the single most useful
  // "is it paper" signal.
  minPaper: 0.25,
  // The bimodality test. Print is dark-on-light: bright paper plus dark ink,
  // with comparatively little in between. Skin, clothing and room scenes are
  // overwhelmingly mid-tone.
  maxMidtone: 0.62,
};

// A SHIPPING LABEL IS NOT A PALE CARD.
//
// The defaults above are tuned for an ID: a bright, high-contrast card filling
// the frame. A parcel is a BROWN BOX with a white label on part of it — the
// paper fraction is a fraction of the frame, and cardboard is exactly the
// mid-tone the bimodality test rejects. Left on the ID thresholds, auto-capture
// would sit on "Lay the ID flat inside the frame" forever and never fire.
//
// Skin stays strict: pointing the camera at the courier instead of the parcel
// is the same mistake, and just as worth refusing.
export const PARCEL_THRESHOLDS: QualityThresholds = {
  ...DEFAULT_THRESHOLDS,
  // Enough label to read, not enough to demand the box be white.
  minPaper: 0.08,
  // Cardboard IS mid-tone. Rejecting mid-tone rejects every parcel.
  maxMidtone: 0.9,
  // A label is dense with small print, so demand a little more edge detail to
  // make up for the looser tone tests — this is what now distinguishes a label
  // from a blank stretch of box.
  detail: 0.02,
};

// The analysis runs on a small copy. 256px wide keeps a full convolution under
// a millisecond while preserving enough high-frequency detail for the focus
// measure to mean anything — going much smaller destroys the very signal being
// measured.
const W = 256;

export class FrameAnalyser {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D | null;
  private prev: Float32Array | null = null;
  private thresholds: QualityThresholds;

  constructor(thresholds: Partial<QualityThresholds> = {}) {
    this.canvas = document.createElement('canvas');
    // willReadFrequently: without it, browsers keep the canvas on the GPU and
    // every getImageData is a stall. This canvas is read on every frame.
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.thresholds = { ...DEFAULT_THRESHOLDS, ...thresholds };
  }

  reset(): void {
    this.prev = null;
  }

  analyse(video: HTMLVideoElement): FrameQuality | null {
    const ctx = this.ctx;
    if (!ctx || !video.videoWidth) return null;

    const h = Math.max(1, Math.round((video.videoHeight / video.videoWidth) * W));
    if (this.canvas.width !== W || this.canvas.height !== h) {
      this.canvas.width = W;
      this.canvas.height = h;
      this.prev = null; // the previous frame is a different shape now
    }

    ctx.drawImage(video, 0, 0, W, h);
    const { data } = ctx.getImageData(0, 0, W, h);

    // ---- greyscale, luminance, and IS THIS A DOCUMENT OR A PERSON ----
    //
    // Sharpness, detail and stillness describe a FACE perfectly — held still,
    // well lit, full of edges. On their own they fire the camera at whoever is
    // holding it. These two tests are what make the difference:
    //
    //   SKIN   in YCbCr, human skin of every tone occupies a narrow, well-known
    //          chroma cluster while varying hugely in luminance. That is why
    //          chroma is used and brightness is not: it holds across skin
    //          tones, which a naive RGB range does not.
    //   TONE   print is dark-on-light. Pale card stock plus dark text gives a
    //          BIMODAL histogram — lots of bright, some dark, little between.
    //          A face, a jumper or a room is overwhelmingly mid-tone.
    const grey = new Float32Array(W * h);
    let sum = 0;
    let skinCount = 0;
    let paperCount = 0;
    let midCount = 0;

    for (let i = 0, p = 0; i < data.length; i += 4, p++) {
      const r = data[i];
      const g8 = data[i + 1];
      const b = data[i + 2];

      // Rec. 601 luma.
      const y = 0.299 * r + 0.587 * g8 + 0.114 * b;
      grey[p] = y;
      sum += y;

      // Chroma, same transform.
      const cb = 128 - 0.168736 * r - 0.331264 * g8 + 0.5 * b;
      const cr = 128 + 0.5 * r - 0.418688 * g8 - 0.081312 * b;
      // The classic Chai & Ngan bounds. Deliberately a little generous — a
      // false "that is a person" costs one extra second of waiting, while a
      // false "that is a document" costs a billed request and a photograph of
      // the concierge attached to a resident's name.
      if (cb >= 77 && cb <= 127 && cr >= 133 && cr <= 173) skinCount++;

      if (y > 175) paperCount++;
      else if (y > 105) midCount++;
    }

    const n0 = grey.length;
    const brightness = sum / n0;
    const skin = skinCount / n0;
    const paper = paperCount / n0;
    const midtone = midCount / n0;

    // ---- Laplacian: sharpness (variance) and detail (edge density) ----
    // 4-neighbour kernel [0 1 0; 1 -4 1; 0 1 0]. Borders are skipped rather
    // than padded — a padded border invents a hard edge and inflates both
    // measures on every frame equally, which is exactly the kind of constant
    // bias that makes a threshold meaningless.
    let lapSum = 0;
    let lapSqSum = 0;
    let strong = 0;
    let n = 0;
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < W - 1; x++) {
        const i = y * W + x;
        const lap =
          grey[i - 1] + grey[i + 1] + grey[i - W] + grey[i + W] - 4 * grey[i];
        lapSum += lap;
        lapSqSum += lap * lap;
        if (lap > 20 || lap < -20) strong++;
        n++;
      }
    }
    const mean = lapSum / n;
    const sharpness = lapSqSum / n - mean * mean;
    const detail = strong / n;

    // ---- stillness ----
    // No previous frame yet: report "moving" rather than "still". Guessing
    // still on the first frame would let a capture fire the instant the camera
    // opens, before the user has aimed it at anything.
    let stillness = Number.POSITIVE_INFINITY;
    if (this.prev && this.prev.length === grey.length) {
      let diff = 0;
      for (let i = 0; i < grey.length; i++) diff += Math.abs(grey[i] - this.prev[i]);
      stillness = diff / grey.length;
    }
    this.prev = grey;

    const t = this.thresholds;
    let hint = '';
    // Ordered by what is worth fixing FIRST.
    //
    // THE DOCUMENT TESTS COME BEFORE THE FOCUS TESTS, deliberately. Telling
    // someone to "hold steady" while the camera is pointed at their face is
    // advice toward the wrong goal — and the moment they succeed in holding
    // steady, it fires. Establish that this is a document at all, then worry
    // about whether it is a good photograph of one.
    if (brightness < t.minBrightness) hint = 'Too dark — move into better light';
    else if (brightness > t.maxBrightness) hint = 'Too bright — tilt away from the glare';
    else if (skin > t.maxSkin) hint = 'Point the camera at the document, not at a person';
    else if (paper < t.minPaper || midtone > t.maxMidtone) {
      hint = 'Fill the frame with the document';
    } else if (stillness > t.stillness) hint = 'Hold steady';
    else if (detail < t.detail) hint = 'Move closer — fill the frame';
    else if (sharpness < t.sharpness) hint = 'Focusing — hold still';

    return {
      sharpness,
      detail,
      stillness,
      brightness,
      skin,
      paper,
      midtone,
      ready: hint === '',
      hint,
    };
  }
}
